// =============================================================================
// components/KpiIncidents/IncidentDashboard.jsx
//
// Dashboard de incidentes com vista linked entre tabela e mapa.
// Layout dinâmico que se ajusta a qualquer resolução vertical e horizontal.
// =============================================================================

import { useEffect, useMemo, useState } from 'react';

import {
	Box,
	Stack,
	Alert,
	CircularProgress,
} from '@mui/material';

import {
	Group,
	Panel,
	Separator,
} from 'react-resizable-panels';

import {
	useIncidents,
	useLookups,
	useSites,
} from '../../hooks/useKpi';

import IncidentFilters     from './IncidentFilters';
import IncidentDetailPanel from './IncidentDetailPanel';
import IncidentTable       from './IncidentTable';
import IncidentMap         from './IncidentMap';

export default function IncidentDashboard() {

	// --------------------------------------------------------------
	// Hooks de dados
	// --------------------------------------------------------------
	const incidents = useIncidents();
	const lookups   = useLookups();
	const sites     = useSites();

	useEffect(function () {
		incidents.load();
		lookups.load();
		sites.load();
	}, []);

	// --------------------------------------------------------------
	// Estado local (UI)
	// --------------------------------------------------------------
	const [selectedSiteId, setSelectedSiteId] = useState(null);

	// --------------------------------------------------------------
	// Derivados
	// --------------------------------------------------------------
	const visibleItems = useMemo(function () {
		if (!selectedSiteId) return incidents.filteredItems;
		return incidents.filteredItems.filter(function (item) {
			return item.siteId === selectedSiteId;
		});
	}, [incidents.filteredItems, selectedSiteId]);

	const flyToCoords = useMemo(function () {
		if (!incidents.selected) return null;
		const key = incidents.selected.vendor + '|' + incidents.selected.siteId;
		const site = sites.siteMap.get(key);
		if (!site) return null;
		return {
			lat:  site.latitude,
			lng:  site.longitude,
			zoom: 20,
		};
	}, [incidents.selected, sites.siteMap]);

	// --------------------------------------------------------------
	// Handlers
	// --------------------------------------------------------------
	const handleSiteClick = function (site) {
		setSelectedSiteId(function (prev) {
			return prev === site.siteId ? null : site.siteId;
		});
	};

	const handleClearSelection = function () {
		setSelectedSiteId(null);
		if (incidents.select) incidents.select(null);
	};

	// --------------------------------------------------------------
	// Estados de loading / erro
	// --------------------------------------------------------------
	if (incidents.loading && !incidents.loaded) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (incidents.error) {
		return <Alert severity="error">{incidents.error}</Alert>;
	}

	// --------------------------------------------------------------
	// Render
	// --------------------------------------------------------------
	return (
		<Stack 
			spacing={2} 
			sx={{ 
				p: 2, 
				height: 'calc(100vh - 70px)', // Ocupa toda a altura disponível da resolução
				minHeight: 550,
				boxSizing: 'border-box'
			}}
		>

			<IncidentFilters
				filters={incidents.filters}
				availableTechs={incidents.availableTechs}
				availableVendors={incidents.availableVendors}
				availableKpis={incidents.availableKpis}
				onChange={incidents.updateFilters}
				onReset={incidents.clearFilters}
				onRefresh={incidents.load}
				loading={incidents.loading}
			/>

			{incidents.selected && (
				<IncidentDetailPanel
					incident={incidents.selected}
					history={incidents.history}
					lookups={lookups}
					onClose={function () { incidents.select(null); }}
				/>
			)}

			{/* Split horizontal responsivo: O flex: 1 garante o ajuste à resolução */}
			<Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
				<Group
					orientation="horizontal"
					defaultLayout={{ 'table-panel': 65, 'map-panel': 35 }} // Tabela mais larga para ficar completa
					style={{ height: '100%', width: '100%' }}
				>

					{/* Tabela */}
					<Panel id="table-panel" minSize="40%">
						<Box sx={{ height: '100%', pr: 0.5, overflow: 'hidden' }}>
							<IncidentTable
								rows={visibleItems}
								loading={incidents.loading}
								onRowClick={incidents.select}
								selected={incidents.selected}
								lookups={lookups}
							/>
						</Box>
					</Panel>

					{/* Separador resizable */}
					<Separator
						style={{
							flexShrink: 0,
							width: 6,
							background: '#e5e7eb',
							cursor: 'col-resize',
							transition: 'background-color 0.15s',
						}}
					/>

					{/* Mapa */}
					<Panel id="map-panel" minSize="25%">
						<Box sx={{ height: '100%', pl: 0.5, overflow: 'hidden' }}>
							<IncidentMap
								items={incidents.filteredItems}
								siteMap={sites.siteMap}
								severityMap={lookups.severityMap}
								statusMap={lookups.statusMap}
								selectedSiteId={selectedSiteId}
								selectedIncident={incidents.selected}
								flyToCoords={flyToCoords}
								onSiteClick={handleSiteClick}
								onClearSelection={handleClearSelection}
							/>
						</Box>
					</Panel>

				</Group>
			</Box>

		</Stack>
	);
}